
import { ProcessDefinition, WorkflowProject, EngineProcess, EngineNode, EngineConnection, AppNode, EngineResource, WorkflowNodeData, EngineRecursiveConnection } from '../types';
import { Edge } from 'reactflow';
import { getEngineType, getExtensionHandler, getHandledFields, resolveEditorType } from './nodeRegistry';

// ID Manager to map UUIDs (Editor) <-> Integers (Engine)
class IdManager {
  private uuidToInt = new Map<string, number>();
  private intToUuid = new Map<number, string>();
  private nextId = 1;

  reset() {
      this.uuidToInt.clear();
      this.intToUuid.clear();
      this.nextId = 1;
  }

  register(uuid: string, id?: number): number {
    const intId = id !== undefined ? id : this.nextId++;
    if (id !== undefined && id >= this.nextId) this.nextId = id + 1;
    
    this.uuidToInt.set(uuid, intId);
    this.intToUuid.set(intId, uuid);
    return intId;
  }

  getInt(uuid: string): number {
    if (!this.uuidToInt.has(uuid)) {
      return this.register(uuid);
    }
    return this.uuidToInt.get(uuid)!;
  }

  getUuid(id: number): string {
    if (!this.intToUuid.has(id)) {
        // Generate a stable UUID if loading from integer ID for the first time
        const newUuid = `node_${id}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
        this.register(newUuid, id);
        return newUuid;
    }
    return this.intToUuid.get(id)!;
  }
}

const idManager = new IdManager();

// Type Mapping: Now uses registry via getEngineType()

// Helper for Timer Kinds
const getTimerKind = (type?: string): number => {
    switch (type) {
        case 'date': return 2;
        case 'cycle': return 3;
        case 'cron': return 3; 
        default: return 1; // duration
    }
};

const getTimerTypeFromKind = (kind?: number): string => {
    switch (kind) {
        case 2: return 'date';
        case 3: return 'cycle';
        default: return 'duration';
    }
};

// Helper for Script Kinds
const getScriptKind = (lang?: string): number => {
    if (lang === 'python') return 6; 
    return 5; // javascript
};

const getScriptLanguageFromKind = (kind?: number): string => {
    if (kind === 6) return 'python';
    return 'javascript';
};

const mapToEngineType = (editorType: string, data: WorkflowNodeData): string => {
    // Handle Boundary Events Distinction
    if (editorType.endsWith('BoundaryEvent')) {
        const isInterrupting = data.cancelActivity !== false;
        if (!isInterrupting) {
            return `NonInterrupting${editorType}`;
        }
    }
    return getEngineType(editorType);
}

const mapFromEngineType = (engineType: string, node: any): string => {
    // Handle Non-Interrupting Boundary Events (Engine -> Editor)
    if (engineType.startsWith('NonInterrupting')) {
        return engineType.replace('NonInterrupting', '');
    }

    // Check for Forms extension to decide if it's a FormStartEvent
    if (engineType === 'BlankStartEvent') {
        if (node.Extensions && node.Extensions.some((e: any) => e.Name === 'PSS.Forms')) {
            return 'FormStartEvent';
        }
        return 'BlankStartEvent';
    }

    // Try registry-based resolution (handles ServiceTask, etc.)
    if (node.Extensions && Array.isArray(node.Extensions)) {
        const resolved = resolveEditorType(engineType, node.Extensions);
        if (resolved) return resolved;
    }

    // Fallback for ServiceTask with no recognized extension
    if (engineType === 'ServiceTask') return 'ServiceTaskREST';

    return engineType;
};

// --- Extension Generators ---

const createFormsExtension = (data: WorkflowNodeData) => {
    const formSchema = data.formSchema;
    if (!formSchema || !formSchema.fields) return null;
    
    return {
        Name: "PSS.Forms",
        Data: {
            Title: data.formTitle,
            Description: data.formDescription,
            FormFields: formSchema.fields.map((f: any) => ({
                Id: f.key || f.id,
                Label: f.label,
                Required: !!f.required,
                ValueType: f.valueType || (f.type === 'number' ? 'long' : f.type === 'boolean' ? 'boolean' : f.type === 'date' ? 'date' : f.type === 'select' ? 'enum' : 'string'),
                Extensions: {
                    input: f.type === 'boolean' ? 'checkbox' : f.type === 'select' ? 'select' : f.type === 'date' ? 'datepicker' : f.type === 'textarea' ? 'textarea' : 'text',
                    options: f.options // Custom property for select options
                }
            }))
        }
    };
};

const createStartProcessExtension = (data: WorkflowNodeData) => {
    return {
        Name: "PSS.StartProcess",
        Data: {
            View: "Workflow::GenericStartView",
            Roles: "*",
            Step: "1",
            Deduplication: "",
            Mode: "end"
        }
    };
};

const createUserTaskExtension = (data: WorkflowNodeData) => {
    const typeMap: Record<string, string> = {
        'generic': 'PSS.GenericUserTask',
        'produce': 'PSS.BadgeProduceTask',
        'smartbridge': 'PSS.SmartBridgeTask',
        'receipt': 'PSS.BadgeReceiptView'
    };
    
    const extName = typeMap[data.userTaskType || 'generic'] || 'PSS.GenericUserTask';
    
    const baseData: any = {
        Assignments: data.assignments || [],
        Mode: "continue",
        Step: 1,
        Rejectable: true,
        Subject: data.label,
        Configuration: data.extensionConfig, // Early-Bound eval JSON script
        Relations: data.extensionRelations,   // Early-Bound eval JSON script
        Priority: data.priority // Moved from __EditorData
    };

    if (data.userTaskType === 'produce') {
        const operations: Record<string, string> = {};
        if (data.productionOperations && Array.isArray(data.productionOperations)) {
            data.productionOperations.forEach(op => {
                if (op.key) operations[op.key] = op.value;
            });
        }

        baseData.Production = {
            ProcessKey: data.productionProcessKey,
            Operations: operations
        };
        baseData.ActorType = "BadgeOffice";
    }

    if (data.userTaskType === 'smartbridge') {
        baseData.Job = data.jobScript; // Early-bound json script
        baseData.ActorType = "SmartBridge";
    }

    if (data.userTaskType === 'receipt') {
        baseData.ActorType = "BadgeOffice";
    }

    // Legacy assignee support
    if ((!data.assignments || data.assignments.length === 0) && data.assignee) {
        baseData.AssigneeLegacy = data.assignee;
    }

    return {
        Name: extName,
        Data: baseData
    };
};

const createRestExtension = (data: WorkflowNodeData) => {
    // Store Auth/Headers in Extensions dictionary within the PSS Task
    const extensions: Record<string, any> = {};
    if (data.headers) extensions.Headers = data.headers;
    if (data.authentication) extensions.Authentication = data.authentication;
    if (data.bearerToken) extensions.BearerToken = data.bearerToken;
    if (data.apiKey) extensions.ApiKey = data.apiKey;

    const restData: Record<string, any> = {
        Topic: data.restTopic || "Query",
        Service: data.service,
        Endpoint: data.endpoint,
        Method: (data.method || 'GET').toUpperCase(),
        Body: data.requestBody,
        ResultVariable: data.resultVariable
    };

    if (typeof data.retries === 'number') restData.Retries = data.retries;
    if (typeof data.onException === 'number') restData.OnException = data.onException;
    if (data.actorType) restData.ActorType = data.actorType;
    if (Object.keys(extensions).length > 0) restData.Extensions = extensions;

    return {
        Name: "PSS.RestTask",
        Data: restData
    };
};

const createEmailExtension = (data: WorkflowNodeData) => {
    const recipients: any[] = [];
    if (data.to) {
        const emails = data.to.split(',').map((s: string) => s.trim());
        const names = (data.toNames || '').split(',').map((s: string) => s.trim());
        emails.forEach((email: string, i: number) => {
            recipients.push({ Kind: 'TO', Email: email, Name: names[i] || email });
        });
    }
    if (data.cc) {
        const emails = data.cc.split(',').map((s: string) => s.trim());
        const names = (data.ccNames || '').split(',').map((s: string) => s.trim());
        emails.forEach((email: string, i: number) => {
            recipients.push({ Kind: 'CC', Email: email, Name: names[i] || email });
        });
    }
    if (data.bcc) {
        const emails = data.bcc.split(',').map((s: string) => s.trim());
        const names = (data.bccNames || '').split(',').map((s: string) => s.trim());
        emails.forEach((email: string, i: number) => {
            recipients.push({ Kind: 'BCC', Email: email, Name: names[i] || email });
        });
    }

    return {
        Name: "PSS.EmailTask",
        Data: {
            Topic: "Mail",
            SenderMail: data.fromEmail,
            SenderName: data.fromName,
            Receipients: recipients,
            Subject: data.subject,
            Content: data.body,
            IsHtml: data.isHtml,
            Attachments: data.attachments?.map((a: string) => ({ Url: a })) || []
        }
    };
};

const createSmsExtension = (data: WorkflowNodeData) => {
    return {
        Name: "PSS.SmsTask",
        Data: {
            Topic: "SMS",
            Receiver: data.toPhone,
            Content: data.message
        }
    };
};

const createDatabaseExtension = (data: WorkflowNodeData) => {
    return {
        Name: "PSS.DatabaseTask",
        Data: {
            Topic: "Database",
            ConnectionId: data.connectionId,
            QueryType: data.queryType,
            Query: data.query,
            ResultVariable: data.resultVariable,
            Timeout: data.timeout,
            Parameters: data.parameters // {Key, Value, Type} array
        }
    };
};

const createMcpExtension = (data: WorkflowNodeData) => {
    return {
        Name: "PSS.McpTask",
        Data: {
            Topic: "MCP",
            ServerUrl: data.mcpServerUrl,
            ServerName: data.mcpServerName,
            Operation: data.mcpOperation,
            Headers: data.headers, // Reusing generic headers field
            ToolName: data.mcpToolName,
            ToolArguments: data.mcpToolArguments,
            ResourceUri: data.mcpResourceUri,
            PromptName: data.mcpPromptName,
            PromptArguments: data.mcpPromptArguments,
            Timeout: data.timeout,
            ResultVariable: data.resultVariable
        }
    };
};

const createAgentExtension = (data: WorkflowNodeData) => {
    return {
        Name: "PSS.AgentTask",
        Data: {
            Topic: "Agent",
            Model: data.model,
            SystemPrompt: data.systemPrompt,
            UserPrompt: data.userPrompt,
            OutputVariable: data.outputVariable
        }
    };
};

const createDecisionExtension = (data: WorkflowNodeData) => {
    return {
        Name: "PSS.DecisionTask",
        Data: {
            Topic: "Command",
            Filename: data.decisionFile,
            InputMapping: data.inputMapping,
            ResultVariable: data.resultVariable,
            // Cache schema for simulation/variables
            OutputSchema: data._outputSchema
        }
    };
};

const createDataExtractionExtension = (data: WorkflowNodeData) => {
    const hasRules = data.extractionRules && data.extractionRules.length > 0;
    const hasUnpackAll = data.unpackAll === true;
    if (!hasRules && !hasUnpackAll && !data.exampleResponse) return null;

    return {
        Name: "PSS.DataExtraction",
        Data: {
            Example: data.exampleResponse,
            ...(hasUnpackAll ? { UnpackAll: true } : {}),
            Rules: (data.extractionRules || []).map(rule => ({
                Method: rule.method || 'jsonpath',
                Source: rule.source,
                Target: rule.targetVar,
                Type: rule.type || 'auto',
                ...(rule.defaultValue !== undefined && rule.defaultValue !== null && rule.defaultValue !== ''
                    ? { DefaultValue: rule.defaultValue }
                    : {})
            }))
        }
    };
};

// --- Extension Parsers ---

const parseFormsExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    
    data.formTitle = ext.Data.Title;
    data.formDescription = ext.Data.Description;

    if (ext.Data.FormFields) {
        data.formSchema = {
            fields: ext.Data.FormFields.map((f: any) => ({
                key: f.Id,
                label: f.Label,
                required: f.Required,
                valueType: f.ValueType,
                type: f.Extensions?.input === 'textarea' ? 'textarea' :
                      f.Extensions?.input === 'select' ? 'select' :
                      f.Extensions?.input === 'checkbox' || f.ValueType === 'boolean' ? 'boolean' :
                      f.Extensions?.input === 'datepicker' || f.Extensions?.input === 'date' || f.ValueType === 'date' ? 'date' :
                      f.Extensions?.input === 'email' || f.ValueType === 'email' ? 'email' :
                      f.Extensions?.input === 'guid' || f.ValueType === 'guid' || f.ValueType === 'uuid' ? 'guid' :
                      f.ValueType === 'long' || f.ValueType === 'number' ? 'number' : 'text',
                options: f.Extensions?.options
            }))
        };
    }
};

const parseUserTaskExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    
    if (ext.Name === 'PSS.GenericUserTask') data.userTaskType = 'generic';
    if (ext.Name === 'PSS.BadgeProduceTask') {
        data.userTaskType = 'produce';
        if (ext.Data.Production) {
            data.productionProcessKey = ext.Data.Production.ProcessKey;
            
            // Map Operations object { Key: Script } to Array [{key, value}]
            if (ext.Data.Production.Operations) {
                data.productionOperations = Object.entries(ext.Data.Production.Operations).map(([key, value]) => ({
                    key,
                    value: value as string
                }));
            } else {
                data.productionOperations = [];
            }
        }
    }
    if (ext.Name === 'PSS.SmartBridgeTask') {
        data.userTaskType = 'smartbridge';
        data.jobScript = ext.Data.Job;
    }
    if (ext.Name === 'PSS.BadgeReceiptView') data.userTaskType = 'receipt';

    data.assignments = ext.Data.Assignments || [];
    data.extensionConfig = ext.Data.Configuration;
    data.extensionRelations = ext.Data.Relations;

    if (ext.Data.AssigneeLegacy) data.assignee = ext.Data.AssigneeLegacy;
    if (ext.Data.Priority) data.priority = ext.Data.Priority;
};

const parseRestExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    const reverseMethodMap = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

    data.restTopic = ext.Data.Topic;
    data.service = ext.Data.Service;
    data.endpoint = ext.Data.Endpoint || ext.Data.EndPoint;
    data.method = typeof ext.Data.Method === 'number' ? reverseMethodMap[ext.Data.Method] || 'GET' : ext.Data.Method;
    data.retries = typeof ext.Data.Retries === 'number' ? ext.Data.Retries : undefined;
    data.onException = typeof ext.Data.OnException === 'number' ? ext.Data.OnException : undefined;
    data.actorType = typeof ext.Data.ActorType === 'string' ? ext.Data.ActorType : undefined;
    data.requestBody = ext.Data.Body;
    data.resultVariable = ext.Data.ResultVariable;
    
    if (ext.Data.Extensions) {
        data.headers = ext.Data.Extensions.Headers;
        data.authentication = ext.Data.Extensions.Authentication;
        data.bearerToken = ext.Data.Extensions.BearerToken;
        data.apiKey = ext.Data.Extensions.ApiKey;
    }
};

const parseEmailExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.fromEmail = ext.Data.SenderMail;
    data.fromName = ext.Data.SenderName;
    data.subject = ext.Data.Subject;
    data.body = ext.Data.Content;
    data.isHtml = ext.Data.IsHtml;
    
    if (ext.Data.Receipients) {
        data.to = ext.Data.Receipients.filter((r: any) => r.Kind === 'TO').map((r: any) => r.Email).join(', ');
        data.toNames = ext.Data.Receipients.filter((r: any) => r.Kind === 'TO').map((r: any) => r.Name).join(', ');
        data.cc = ext.Data.Receipients.filter((r: any) => r.Kind === 'CC').map((r: any) => r.Email).join(', ');
        data.ccNames = ext.Data.Receipients.filter((r: any) => r.Kind === 'CC').map((r: any) => r.Name).join(', ');
        data.bcc = ext.Data.Receipients.filter((r: any) => r.Kind === 'BCC').map((r: any) => r.Email).join(', ');
        data.bccNames = ext.Data.Receipients.filter((r: any) => r.Kind === 'BCC').map((r: any) => r.Name).join(', ');
    }
    
    if (ext.Data.Attachments) {
        data.attachments = ext.Data.Attachments.map((a: any) => a.Url);
    }
};

const parseSmsExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.toPhone = ext.Data.Receiver;
    data.message = ext.Data.Content;
};

const parseDatabaseExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.connectionId = ext.Data.ConnectionId;
    data.queryType = ext.Data.QueryType;
    data.query = ext.Data.Query;
    data.resultVariable = ext.Data.ResultVariable;
    data.timeout = ext.Data.Timeout;
    data.parameters = ext.Data.Parameters;
};

const parseMcpExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.mcpServerUrl = ext.Data.ServerUrl;
    data.mcpServerName = ext.Data.ServerName;
    data.mcpOperation = ext.Data.Operation;
    data.headers = ext.Data.Headers;
    data.mcpToolName = ext.Data.ToolName;
    data.mcpToolArguments = ext.Data.ToolArguments;
    data.mcpResourceUri = ext.Data.ResourceUri;
    data.mcpPromptName = ext.Data.PromptName;
    data.mcpPromptArguments = ext.Data.PromptArguments;
    data.timeout = ext.Data.Timeout;
    data.resultVariable = ext.Data.ResultVariable;
};

const parseAgentExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.model = ext.Data.Model;
    data.systemPrompt = ext.Data.SystemPrompt;
    data.userPrompt = ext.Data.UserPrompt;
    data.outputVariable = ext.Data.OutputVariable;
};

const parseDecisionExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.decisionFile = ext.Data.Filename;
    data.inputMapping = ext.Data.InputMapping;
    data.resultVariable = ext.Data.ResultVariable;
    data._outputSchema = ext.Data.OutputSchema;
};

const parseDataExtractionExtension = (ext: any, data: WorkflowNodeData) => {
    if (!ext.Data) return;
    data.exampleResponse = ext.Data.Example;
    if (ext.Data.UnpackAll === true) data.unpackAll = true;
    if (ext.Data.Rules && Array.isArray(ext.Data.Rules)) {
        data.extractionRules = ext.Data.Rules.map((r: any) => ({
            method: r.Method || (ext.Data.Format === 'xml' ? 'xpath' : ext.Data.Format === 'text' ? 'regex' : 'jsonpath'),
            source: r.Source,
            targetVar: r.Target,
            type: r.Type,
            ...(r.DefaultValue !== undefined && r.DefaultValue !== null ? { defaultValue: r.DefaultValue } : {})
        }));
    }
};

/**
 * Generates the Workflow Project object (PSS) from the current editor state.
 * This is the pure data generation part, suitable for both saving to file and passing to callbacks.
 */
export const generateWorkflowProject = (processes: ProcessDefinition[], projectName: string = "Workflow Project"): WorkflowProject => {
    const resources: Record<string, EngineResource> = {};
    let resourceCounter = 1;

    const engineProcesses: EngineProcess[] = processes.map(proc => {
        idManager.reset();
        
        // 1. Register all nodes to ensure IDs are available for references
        proc.nodes.forEach(node => idManager.register(node.id));

        // 2. Process Nodes
        const engineNodes: EngineNode[] = proc.nodes.map(node => {
            const intId = idManager.getInt(node.id);
            // Destructure to separate core properties and extension-handled data from generic editor data
            // Any property NOT listed here will end up in restData and subsequently in __EditorData
            const { 
                // Core Fields
                label, description,
                messageName, messageVariableName, messagePayloadVariableName, messagePayload,
                signalName, timerDefinition,
                script, 
                resultVariable, 
                isMerging, 
                exceptionType, 
                conditions, 
                outputs, 
                status, 
                
                // Form Fields (Handled by PSS.Forms)
                formSchema, formTitle, formDescription,

                // User Task Fields (Handled by PSS.*UserTask)
                userTaskType, assignments, extensionConfig, extensionRelations,
                productionProcessKey, productionOperations, jobScript, assignee, priority,

                // REST Fields (Handled by PSS.RestTask)
                restTopic, service, endpoint, method, retries, onException, actorType, requestBody, headers, authentication, bearerToken, apiKey,

                // Email Fields (Handled by PSS.EmailTask)
                fromEmail, fromName, to, toNames, cc, ccNames, bcc, bccNames, subject, body, isHtml, attachments,

                // SMS Fields (Handled by PSS.SmsTask)
                toPhone, message,

                // DB Fields (Handled by PSS.DatabaseTask)
                connectionId, queryType, query, timeout, parameters,

                // LLM Fields (Handled by PSS.AgentTask)
                model, systemPrompt, userPrompt, outputVariable,

                // MCP Fields (Handled by PSS.McpTask)
                mcpServerUrl, mcpServerName, mcpOperation, mcpToolName, mcpToolArguments,
                mcpResourceUri, mcpPromptName, mcpPromptArguments,

                // Decision Fields (Handled by PSS.DecisionTask)
                decisionFile, inputMapping, _outputSchema,

                // Call Activity Fields
                processName, processCollection, processVersion, keepBusinessKey, sequentialLoop, asAsyncCall, calledElement,

                // Extraction (Handled by PSS.DataExtraction)
                extractionRules, exampleResponse,

                // Script (Handled by EngineNode.Script Property)
                scriptLanguage,

                // Timer (Handled by EngineNode.Timer/Kind)
                timerType, timerDate, endDate, timezone,

                // Editor specific leftovers that should NOT be serialized
                isConnectableTarget, isBoundaryCandidate, isPotentialBoundaryTarget, categoryLabel,

                // Boundaries (Handled by type + Activity)
                cancelActivity,

                // Error Handling (ErrorEndEvent)
                errorMessage,
                errorPayload,

                // Everything else remains in restData for __EditorData
                ...restData 
            } = node.data;

            const engineType = mapToEngineType(node.type || 'Task', node.data);

            const extensions: any[] = [];

            // Remove registry-handled fields from restData (custom node extensions)
            const handledFields = getHandledFields(node.type || '');
            handledFields.forEach(f => delete restData[f]);

            // Add __EditorData only if there is residual data not covered by other extensions
            // or if we need to preserve specific UI state like custom colors (if implemented later)
            // Note: scriptLanguage is preserved here in restData
            if (Object.keys(restData).length > 0) {
                extensions.push({
                    Name: "__EditorData",
                    Data: JSON.stringify({
                        data: restData
                    })
                });
            }

            // --- Generate PSS Extensions ---
            if (node.data.formSchema) {
                const formsExt = createFormsExtension(node.data);
                if (formsExt) extensions.push(formsExt);

                if (node.type === 'FormStartEvent') {
                    extensions.push(createStartProcessExtension(node.data));
                }
            }

            // Built-in node types with special create functions
            if (node.type === 'UserTask') {
                const userExt = createUserTaskExtension(node.data);
                if (userExt) extensions.push(userExt);
            } else if (node.type === 'ServiceTaskREST') {
                extensions.push(createRestExtension(node.data));
            } else if (node.type === 'ServiceTaskEmail') {
                extensions.push(createEmailExtension(node.data));
            } else if (node.type === 'ServiceTaskSMS') {
                extensions.push(createSmsExtension(node.data));
            } else if (node.type === 'ServiceTaskDB') {
                extensions.push(createDatabaseExtension(node.data));
            } else if (node.type === 'ServiceTaskLLM') {
                extensions.push(createAgentExtension(node.data));
            } else if (node.type === 'ServiceTaskMCP') {
                extensions.push(createMcpExtension(node.data));
            } else if (node.type === 'ServiceTaskRules') {
                extensions.push(createDecisionExtension(node.data));
            } else {
                // Registry-based extension creation for custom nodes
                const handler = getExtensionHandler(node.type || '');
                if (handler) {
                    const ext = handler.create(node.data);
                    if (ext) extensions.push(ext);
                }
            }

            // Data Extraction can apply to any node type that returns data
            const extractionExt = createDataExtractionExtension(node.data);
            if (extractionExt) extensions.push(extractionExt);

            // Engine coordinates should ideally be absolute for generic tools
            let absoluteX = node.position.x;
            let absoluteY = node.position.y;
            if (node.parentNode) {
                const parent = proc.nodes.find(n => n.id === node.parentNode);
                if (parent) {
                    absoluteX += parent.position.x;
                    absoluteY += parent.position.y;
                }
            }

            // Base Node
            const engineNode: EngineNode = {
                type: engineType,
                Id: intId,
                Key: node.id, // Store string ID (Key) for persistence
                X: Math.round(absoluteX),
                Y: Math.round(absoluteY),
                Text: label,
                comment: description,
                Extensions: extensions
            };

            // --- Property Mapping (Core Fields) ---
            if (node.data.messageName) {
                if (node.data.messageVariableName || node.data.messagePayloadVariableName || node.data.messagePayload) {
                    engineNode.Message = {
                        StaticText: node.data.messageName,
                        VariableName: node.data.messageVariableName,
                        PayloadVariableName: node.data.messagePayloadVariableName,
                        Payload: node.data.messagePayload,
                    };
                } else {
                    engineNode.Message = node.data.messageName;
                }
            }
            if (node.data.signalName) engineNode.Signal = node.data.signalName;
            
            // Map Timer + Type to Engine Node
            if (node.data.timerDefinition) {
                engineNode.Timer = node.data.timerDefinition;
                engineNode.Kind = getTimerKind(node.data.timerType);
            } else if (node.data.timerDate) {
                // If it's a date timer, put date in Timer string, set kind 2
                engineNode.Timer = node.data.timerDate;
                engineNode.Kind = 2; // Date
            }

            if (node.data.script) {
                engineNode.Script = node.data.script;
            }
            // Always write ResultVariable to engine node — the workflow engine reads it
            // from the service task directly, not from extensions
            if (node.data.resultVariable) {
                engineNode.ResultVariable = node.data.resultVariable;
            }

            // Call Activity Specifics
            if (node.type === 'CallActivity') {
                engineNode.ProcessName = {
                    Name: processName || '',
                    Collection: processCollection || '',
                    Version: processVersion || 0
                };
                engineNode.KeepBusinessKey = !!keepBusinessKey;
                engineNode.SequentialLoop = !!sequentialLoop;
                engineNode.AsAsyncCall = !!asAsyncCall;
                // Also support input/output variables mapping if needed, usually done via Extensions or dedicated fields
                // For now, these might be in __EditorData or require a specific extension like 'PSS.CallActivity' if engine requires it
                // The prompt only asked for ProcessName and bool props on the node itself.
            }

            // Boundary Events - Attached to Activity
            if (node.type?.endsWith('BoundaryEvent')) {
                if (node.parentNode) {
                    engineNode.Activity = idManager.getInt(node.parentNode);
                }
                
                // Set ExceptionType and ErrorMessage for error boundaries
                if (node.type === 'ErrorBoundaryEvent') {
                    if (node.data.exceptionType) engineNode.ExceptionType = node.data.exceptionType;
                    if (node.data.errorMessage) engineNode.ErrorMessage = node.data.errorMessage;
                }
            }
            
            // Activities - Boundary Events List
            const attachedBoundaries = proc.nodes.filter(n => n.parentNode === node.id);
            if (attachedBoundaries.length > 0) {
                engineNode.BoundaryEvents = attachedBoundaries.map(n => idManager.getInt(n.id));
            }

            // Gateways
            if (node.type === 'XorGateway' || node.type === 'InclusiveGateway') {
                engineNode.IsMerging = !!node.data.isMerging;
                
                // Export DefaultPath logic
                // Implicit: Any path with an empty expression is the default path
                if (node.data.conditions && !engineNode.IsMerging) {
                    const defaultCondition = node.data.conditions.find((c: any) => !c.expression || c.expression.trim() === '');
                    if (defaultCondition) {
                         const defaultEdge = proc.edges.find(e => e.source === node.id && e.sourceHandle === String(defaultCondition.path));
                         if (defaultEdge) {
                             engineNode.DefaultPath = idManager.getInt(defaultEdge.target);
                         }
                    }
                }
                
                if (node.data.conditions) {
                    engineNode.Expressions = {};
                    engineNode.ExpressionMapping = {};
                    
                    const outgoingEdges = proc.edges.filter(e => e.source === node.id);
                    
                    node.data.conditions.forEach((cond: any, index: number) => {
                        // Use output label as expression name if valid, else cond_N
                        let exprName = cond.label || `cond_${index}`;
                        
                        // Use label from outputs array if available (preferred)
                        if (node.data.outputs && node.data.outputs[index]) {
                            exprName = node.data.outputs[index];
                        }

                        if (cond.expression) {
                            if (!engineNode.Expressions) engineNode.Expressions = {};
                            engineNode.Expressions[exprName] = cond.expression;
                        }

                        const edge = outgoingEdges.find(e => e.sourceHandle === String(cond.path));
                        if (edge) {
                            const targetId = String(idManager.getInt(edge.target));
                            if (!engineNode.ExpressionMapping) engineNode.ExpressionMapping = {};
                            engineNode.ExpressionMapping[targetId] = exprName;
                        }
                    });
                }
            }
            
            if (node.type === 'ParallelGateway') {
                engineNode.IsMerging = !!node.data.isMerging;
            }

            // Error End Event
            if (node.type === 'ErrorEndEvent') {
                if (errorMessage) engineNode.Message = errorMessage;
                if (errorPayload) engineNode.Error = errorPayload;
            }

            return engineNode;
        });

        // 3. Process Connections
        const engineConnections: EngineConnection[] = [];
        const engineRecursiveConnections: EngineRecursiveConnection[] = [];

        proc.edges.forEach(edge => {
            const targetNode = proc.nodes.find(n => n.id === edge.target);
            if (targetNode?.type?.endsWith('BoundaryEvent') && targetNode.parentNode === edge.source) {
                return;
            }

            const fromId = idManager.getInt(edge.source);
            const toId = idManager.getInt(edge.target);

            // Separate recursive edges from regular connections
            if ((edge as any).data?.isRecursive) {
                engineRecursiveConnections.push({ From: fromId, To: toId });
                return;
            }

            const sourceNode = proc.nodes.find(n => n.id === edge.source);

            const conn: EngineConnection = {
                From: fromId,
                To: toId,
                StartPoint: 1,
                EndPoint: 3,
                IsDefault: false
            };

            // Check if this edge label should be exported as ExpressionName
            const hasExpression = edge.label && typeof edge.label === 'string' && edge.label.trim() !== '';

            if (hasExpression) {
                conn.ExpressionName = edge.label as string;
                conn.IsDefault = false;
            } else {
                // If it's a gateway (XOR/Inclusive), and has no expression, it's the default path
                if (sourceNode && (sourceNode.type === 'XorGateway' || sourceNode.type === 'InclusiveGateway')) {
                    conn.IsDefault = true;
                }
            }

            engineConnections.push(conn);
        });

        return {
            Name: proc.name,
            IsSubProcess: !!proc.isSubProcess,
            Nodes: engineNodes,
            Connections: engineConnections,
            RecursiveConnections: engineRecursiveConnections
        };
    });

    return {
        Name: projectName,
        Version: 1,
        Resources: resources,
        Processes: engineProcesses
    };
};

/**
 * Parses a Workflow Project object (PSS) into Editor Process Definitions.
 */
export const parseWorkflowProject = (project: WorkflowProject): ProcessDefinition[] => {
    return project.Processes.map((proc, procIndex) => {
        idManager.reset();
        const engineNodeMap = new Map<number, EngineNode>();
        const childToParentMap = new Map<number, number>();

        // Map to store gateway logic for later edge connection
        // Map<SourceEngineId, Map<TargetEngineId, HandleIndexString>>
        const gatewayEdgeMap = new Map<number, Map<number, string>>();
        // Map<SourceEngineId, Map<TargetEngineId, OutputLabel>> for edge labels
        const gatewayLabelMap = new Map<number, Map<number, string>>();

        if (proc.Nodes) {
            proc.Nodes.forEach(n => {
                engineNodeMap.set(n.Id, n);
                if (n.Activity) childToParentMap.set(n.Id, n.Activity);
                if (n.BoundaryEvents) n.BoundaryEvents.forEach(childId => childToParentMap.set(childId, n.Id));
            });
        }

        const nodes: AppNode[] = (proc.Nodes || []).map((node, nodeIdx) => {
            let editorData: any = null;
            let editorType: string | null = null;
            let savedPosition: { x: number, y: number } | null = null;
            
            // 1. Try to restore exact editor state from __EditorData if available
            if (node.Extensions && Array.isArray(node.Extensions)) {
                const ext = node.Extensions.find((e: any) => (e.Name || e.Key) === '__EditorData');
                if (ext && (ext.Data || ext.Value)) {
                    try {
                        const parsed = JSON.parse(ext.Data || ext.Value);
                        editorData = parsed.data;
                        editorType = parsed.type;
                        savedPosition = parsed.position;
                    } catch (e) {}
                }
            }

            // Prefer Key if available (it contains the original string ID), otherwise generate stable UUID from Integer ID
            let uuid = node.Key;
            if (!uuid) {
                uuid = idManager.getUuid(node.Id);
            }
            idManager.register(uuid, node.Id);

            // 2. If no editor state, or to ensure sync with engine state, parse PSS Extensions
            if (!editorData) {
                editorData = {};
            }
            
            // Always default status to idle on load, as status is transient
            if (!editorData.status) {
                editorData.status = 'idle';
            }

            // Restore core properties that were stripped from __EditorData
            if (!editorData.label && node.Text) editorData.label = node.Text;
            if (!editorData.description && node.comment) editorData.description = node.comment;

            // Determine Type if not set by EditorData
            if (!editorType) {
                editorType = mapFromEngineType(node.type, node);
            }

            // Apply extensions overwriting any stale EditorData
            if (node.Extensions) {
                node.Extensions.forEach(ext => {
                    // Built-in extension parsers
                    if (ext.Name === 'PSS.Forms') parseFormsExtension(ext, editorData);
                    if (ext.Name === 'PSS.RestTask') parseRestExtension(ext, editorData);
                    if (ext.Name === 'PSS.EmailTask') parseEmailExtension(ext, editorData);
                    if (ext.Name === 'PSS.SmsTask') parseSmsExtension(ext, editorData);
                    if (ext.Name === 'PSS.DatabaseTask') parseDatabaseExtension(ext, editorData);
                    if (ext.Name === 'PSS.McpTask') parseMcpExtension(ext, editorData);
                    if (ext.Name === 'PSS.AgentTask') parseAgentExtension(ext, editorData);
                    if (ext.Name === 'PSS.DecisionTask') parseDecisionExtension(ext, editorData);
                    if (ext.Name === 'PSS.DataExtraction') parseDataExtractionExtension(ext, editorData);

                    if (['PSS.GenericUserTask', 'PSS.BadgeProduceTask', 'PSS.SmartBridgeTask', 'PSS.BadgeReceiptView'].includes(ext.Name)) {
                        parseUserTaskExtension(ext, editorData);
                    }

                    // Registry-based extension parsers (custom nodes)
                    if (editorType && ext.Name) {
                        const handler = getExtensionHandler(editorType);
                        if (handler && handler.name === ext.Name) {
                            handler.parse(ext, editorData);
                        }
                    }
                });
            }

            // Handle Non-Interrupting flag based on original engine type if not set
            if (node.type.startsWith('NonInterrupting')) {
                editorData.cancelActivity = false;
            } else if (editorType?.endsWith('BoundaryEvent') && editorData.cancelActivity === undefined) {
                // If it is a boundary event and NOT non-interrupting engine type, 
                // ensure cancelActivity is true (default)
                editorData.cancelActivity = true;
            }

            // Gateway Logic Reconstruction
            // Since we stripped conditions/outputs from __EditorData, we MUST reconstruct them here
            if (editorType === 'XorGateway' || editorType === 'InclusiveGateway' || editorType === 'ParallelGateway') {
                editorData.isMerging = !!node.IsMerging;

                if (!node.IsMerging) {
                    // Find outgoing connections for this node (include recursive connections)
                    const outgoing = [
                        ...(proc.Connections || []).filter(c => c.From === node.Id),
                        ...(proc.RecursiveConnections || []).filter(c => c.From === node.Id),
                    ];
                    
                    const outputs: string[] = [];
                    const conditions: any[] = [];
                    const targetToHandle = new Map<number, string>();
                    const targetToLabel = new Map<number, string>();

                    outgoing.forEach((conn, idx) => {
                        const targetId = conn.To;
                        const handleId = String(idx);
                        targetToHandle.set(targetId, handleId);

                        // Determine label and expression
                        let label = `Path ${idx + 1}`;
                        let expression = "";

                        const targetIdStr = String(targetId);
                        const connAny = conn as any;

                        // Check ExpressionMapping
                        if (node.ExpressionMapping && node.ExpressionMapping[targetIdStr]) {
                            const exprName = node.ExpressionMapping[targetIdStr];
                            label = exprName;
                            if (node.Expressions && node.Expressions[exprName]) {
                                expression = node.Expressions[exprName];
                            }
                        }
                        // Check Default Path - name it "default" so it's visible in the editor
                        else if (node.DefaultPath === targetId || connAny.IsDefault) {
                            label = "default";
                        }
                        // Check Connection ExpressionName (legacy/alternative)
                        else if (connAny.ExpressionName) {
                            label = connAny.ExpressionName;
                            // Attempt to see if this name maps to an expression code, otherwise leave empty (true)
                            if (node.Expressions && node.Expressions[label]) {
                                expression = node.Expressions[label];
                            }
                        }

                        outputs.push(label);
                        targetToLabel.set(targetId, label);

                        // Add condition entry - default paths get empty expression
                        if (expression && expression.trim() !== '') {
                            conditions.push({ path: idx, label, expression });
                        } else {
                            // Default path: add condition with empty expression so it appears in the editor
                            conditions.push({ path: idx, label, expression: '' });
                        }
                    });

                    editorData.outputs = outputs;
                    editorData.conditions = conditions;
                    if (node.IsMerging !== undefined) editorData.isMerging = node.IsMerging;
                    
                    if (outgoing.length > 0) {
                        gatewayEdgeMap.set(node.Id, targetToHandle);
                        gatewayLabelMap.set(node.Id, targetToLabel);
                    }
                }
            }

            // Parse Core Properties (Fallbacks)
            if (node.Script && typeof node.Script === 'string') {
                // Directly use script content from the node
                editorData.script = node.Script;
                // If scriptLanguage wasn't preserved in editorData, default it
                if (!editorData.scriptLanguage) {
                    editorData.scriptLanguage = 'javascript';
                }
            }
            if (node.Message) {
                if (typeof node.Message === 'string') {
                    editorData.messageName = node.Message;
                } else {
                    editorData.messageName = node.Message.StaticText;
                    editorData.messageVariableName = node.Message.VariableName;
                    editorData.messagePayloadVariableName = node.Message.PayloadVariableName;
                    editorData.messagePayload = node.Message.Payload;
                }
            }
            if (node.Signal) editorData.signalName = node.Signal;
            
            if (node.Timer) {
                editorData.timerDefinition = node.Timer;
                // Infer timer type based on Kind if present, else fallback logic
                if (node.Kind) {
                    editorData.timerType = getTimerTypeFromKind(node.Kind);
                    // Special case for Date: if type is date, the definition IS the date
                    if (editorData.timerType === 'date') {
                        editorData.timerDate = node.Timer;
                    }
                } else if (!editorData.timerType) {
                    // Legacy Fallback
                    const t = node.Timer.trim();
                    if (t.startsWith('R')) editorData.timerType = 'cycle';
                    else if (t.includes(' ') || t.includes('*')) editorData.timerType = 'cron';
                    else if (t.startsWith('P') || t.startsWith('-P')) editorData.timerType = 'cycle'; // Map ISO duration to cycle
                    else if (!isNaN(Date.parse(t)) && t.includes('T')) {
                        editorData.timerType = 'date';
                        editorData.timerDate = t;
                    } else {
                        editorData.timerType = 'cycle'; // Default fallback
                    }
                }
            }

            if (node.ResultVariable && !editorData.resultVariable) editorData.resultVariable = node.ResultVariable;
            if (node.IsMerging !== undefined) editorData.isMerging = node.IsMerging;
            
            if (editorType === 'ErrorBoundaryEvent') {
                if (node.ExceptionType) editorData.exceptionType = node.ExceptionType;
                if (node.ErrorMessage) editorData.errorMessage = node.ErrorMessage;
            }

            if (editorType === 'ErrorEndEvent') {
                if (node.Message && typeof node.Message === 'string') {
                    editorData.errorMessage = node.Message;
                }
                if (node.Error) {
                    editorData.errorPayload = node.Error;
                }
            }

            // Call Activity Parsing
            if (editorType === 'CallActivity') {
                if (node.ProcessName) {
                    editorData.processName = node.ProcessName.Name;
                    editorData.processCollection = node.ProcessName.Collection;
                    editorData.processVersion = node.ProcessName.Version;
                }
                if (node.KeepBusinessKey !== undefined) editorData.keepBusinessKey = node.KeepBusinessKey;
                if (node.SequentialLoop !== undefined) editorData.sequentialLoop = node.SequentialLoop;
                if (node.AsAsyncCall !== undefined) editorData.asAsyncCall = node.AsAsyncCall;
            }

            const parentId = childToParentMap.get(node.Id);
            let parentUuid: string | undefined = undefined;
            if (parentId !== undefined && parentId !== node.Id && engineNodeMap.has(parentId)) {
                parentUuid = idManager.getUuid(parentId);
            }

            // 3. Coordinate Handling
            let position = savedPosition || { x: node.X || 0, y: node.Y || 0 };
            
            if (!savedPosition && parentUuid && parentId) {
                const parent = engineNodeMap.get(parentId);
                if (parent) {
                    position = {
                        x: (node.X || 0) - (parent.X || 0),
                        y: (node.Y || 0) - (parent.Y || 0)
                    };
                }
            }

            return {
                id: uuid,
                type: editorType || 'UserTask',
                position,
                data: editorData,
                parentNode: parentUuid,
                zIndex: editorType?.endsWith('BoundaryEvent') ? 2000 : undefined,
                extent: (parentUuid && !editorType?.endsWith('BoundaryEvent')) ? 'parent' : undefined
            };
        });
        
        nodes.sort((a, b) => {
            const aHasParent = !!a.parentNode;
            const bHasParent = !!b.parentNode;
            if (aHasParent === bHasParent) return 0;
            return aHasParent ? 1 : -1;
        });

        const edges: Edge[] = (proc.Connections || []).map((conn, connIdx) => {
            const source = idManager.getUuid(conn.From);
            const target = idManager.getUuid(conn.To);

            // Determine sourceHandle based on Gateway mapping if available
            let sourceHandle = null;
            if (gatewayEdgeMap.has(conn.From)) {
                const mapping = gatewayEdgeMap.get(conn.From);
                if (mapping?.has(conn.To)) {
                    sourceHandle = mapping.get(conn.To);
                }
            }

            // Use ExpressionName if available, fall back to gateway output label
            let edgeLabel = conn.ExpressionName;
            if (!edgeLabel && gatewayLabelMap.has(conn.From)) {
                edgeLabel = gatewayLabelMap.get(conn.From)?.get(conn.To);
            }

            return {
                id: `e_${source}_${target}_${connIdx}`,
                source,
                target,
                sourceHandle: sourceHandle || null,
                label: edgeLabel,
                animated: true,
                style: { stroke: '#94a3b8' },
                type: 'deletable'
            };
        });

        // Add recursive connections as edges
        (proc.RecursiveConnections || []).forEach((conn, idx) => {
            const source = idManager.getUuid(conn.From);
            const target = idManager.getUuid(conn.To);

            let sourceHandle = null;
            if (gatewayEdgeMap.has(conn.From)) {
                const mapping = gatewayEdgeMap.get(conn.From);
                if (mapping?.has(conn.To)) {
                    sourceHandle = mapping.get(conn.To);
                }
            }

            // Use ExpressionName if available, fall back to gateway output label
            let edgeLabel = (conn as any).ExpressionName;
            if (!edgeLabel && gatewayLabelMap.has(conn.From)) {
                edgeLabel = gatewayLabelMap.get(conn.From)?.get(conn.To);
            }

            edges.push({
                id: `e_rec_${source}_${target}_${idx}`,
                source,
                target,
                sourceHandle: sourceHandle || null,
                label: edgeLabel,
                animated: true,
                style: { stroke: '#94a3b8' },
                type: 'deletable',
                data: { isRecursive: true },
            });
        });

        nodes.forEach(node => {
            if (node.type?.endsWith('BoundaryEvent') && node.parentNode) {
                 const parent = nodes.find(n => n.id === node.parentNode);
                 if (parent) {
                     const edgeId = `e_assoc_${parent.id}_${node.id}`;
                     edges.push({
                         id: edgeId,
                         source: parent.id,
                         target: node.id,
                         sourceHandle: 'boundary',
                         targetHandle: null,
                         type: 'deletable',
                         animated: false,
                         style: { stroke: '#94a3b8' }, 
                         selected: false
                     });
                 }
            }
        });

        return {
            id: `proc_${Date.now()}_${procIndex}`,
            name: proc.Name || `Imported Process ${procIndex + 1}`,
            isSubProcess: !!proc.IsSubProcess,
            description: "",
            nodes,
            edges
        };
    });
};

/**
 * Loads a project from a JSON file, strictly adhering to Unified Engine Schema.
 */
export const loadProjectFromFile = async (file: File): Promise<ProcessDefinition[]> => {
    let text = "";
    try {
        text = await file.text();
    } catch (e) {
        throw new Error("Failed to read file content.");
    }

    if (!text.trim()) {
        throw new Error("File content is empty.");
    }

    let data: any;
    try {
        data = JSON.parse(text);
    } catch (e) {
        throw new Error("File contains invalid JSON syntax.");
    }

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        throw new Error("Invalid PSS format: Root must be a JSON object.");
    }

    const project = data as WorkflowProject;
    return parseWorkflowProject(project);
};

/**
 * Saves the project to a JSON file (browser download).
 */
export const saveProjectToFile = (processes: ProcessDefinition[], fileName: string = "workflow_project") => {
    const project = generateWorkflowProject(processes, fileName);
    const jsonString = JSON.stringify(project, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.bpjs`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
